'use client';

import React from 'react';
import styles from './index.module.css';
import ContentSection from './ContentSection';
import { Container, ContentWrapper } from './styled';
import { CountryConfig } from 'lib/region/getCountrySpecificConfig';
import { BuySectionInfo } from './BuySectionInfo';
import { useHomeContext } from '../HomeContext';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  ProductCarousel,
  ProductCarouselItem,
} from 'components/shared/ProductCarousel';
import { ProductData } from './types';

// Mobile-only info block. Rendered at all breakpoints so the SSR markup is
// correct without a JS width gate, and hidden on desktop via CSS (where the
// content panel takes over). Replaces the old `width < TABLET_MAX` gate that
// rendered the wrong branch on first paint and shifted the layout after
// hydration.
const MobileBuySectionInfo = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.mobileBuySectionInfo}>{children}</div>
);

interface BuySectionProps {
  product: ProductData;
  carouselItems: ProductCarouselItem[];
  countrySpecificConfigData: CountryConfig;
  contentTitle: string;
  defaultStrikedPrice?: boolean;
  defaultOfferText?: string;
  defaultOfferDiscount?: number | string;
  contentText: string;
  ctaText?: string;
  onCtaClick?: () => void;
}

const BuySection: React.FC<BuySectionProps> = ({
  product,
  carouselItems,
  countrySpecificConfigData,
  contentTitle,
  defaultStrikedPrice = false,
  defaultOfferText,
  contentText,
  ctaText,
  onCtaClick,
}) => {
  const {
    store,
    price,
    affiliateDiscount,
    referral,
    shippingEstimate,
    defaultCurrency,
  } = useHomeContext();

  return (
    <Container>
      <MobileBuySectionInfo>
        <BuySectionInfo
          title={contentTitle}
          store={store as ShopifyStore}
          defaultCurrency={defaultCurrency}
          price={price}
          defaultStrikedPrice={defaultStrikedPrice}
          referral={referral}
          shippingEstimate={shippingEstimate}
          affiliateDiscount={affiliateDiscount}
          defaultOfferText={defaultOfferText}
          defaultOfferDiscount={2000}
          secondaryOfferData={countrySpecificConfigData?.secondaryOffers}
        />
      </MobileBuySectionInfo>
      <ProductCarousel
        items={carouselItems}
        sticky={true}
        navigationType="cursorTracked"
        autoSlideDuration={4.2}
        // pauseOnHover={true}
      />
      <ContentWrapper>
        <ContentSection
          product={product}
          title={contentTitle}
          text={contentText}
          ctaText={ctaText}
          onCtaClick={onCtaClick}
          countrySpecificConfigData={countrySpecificConfigData} // Pass the actual config if needed
        />
      </ContentWrapper>
    </Container>
  );
};

export default BuySection;
